import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { MainLayout } from '../MainLayout';
import { useSession } from '../../contexts/SessionContext';
import { supabase } from '../../lib/supabase_client';
import type { Announcement } from '../AnnouncementsBanner';
import '../../styles/page-base.css';

const isEmptyHtml = (html: string) =>
  !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();

const announcementEditorInit = (height: number) => ({
  height,
  menubar: false,
  branding: false,
  statusbar: false,
  plugins: ['lists', 'link', 'autolink', 'code'],
  toolbar:
    'bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | link | removeformat | code',
  content_style:
    'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; line-height: 1.5; }',
  convert_urls: false,
});

type Severity = Announcement['severity'];

interface AnnouncementFormData {
  title: string;
  summary: string;
  detail: string;
  severity: Severity;
  country_iso: string; // '' = all
  placement: string;
  is_active: boolean;
  starts_at: string;
  ends_at: string;
  display_order: number;
}

const emptyForm = (): AnnouncementFormData => ({
  title: '',
  summary: '',
  detail: '',
  severity: 'info',
  country_iso: 'MY',
  placement: 'all_filing',
  is_active: true,
  starts_at: '',
  ends_at: '',
  display_order: 0,
});

const severityBadge = (severity: Severity) => {
  switch (severity) {
    case 'urgent':
      return 'bg-red-100 text-red-700';
    case 'warning':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-blue-100 text-blue-700';
  }
};

const toLocalInputValue = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInputValue = (value: string) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default function AdminAnnouncements() {
  const navigate = useNavigate();
  const { isAdmin, loading: sessionLoading } = useSession();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [form, setForm] = useState<AnnouncementFormData>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<{ iso: string; name: string }[]>([]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching announcements:', error);
        setAnnouncements([]);
        return;
      }
      setAnnouncements((data || []) as Announcement[]);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    const { data, error } = await supabase
      .from('countries')
      .select('iso, name')
      .order('name', { ascending: true });
    if (!error) setCountries(data || []);
  }, []);

  useEffect(() => {
    if (!sessionLoading && isAdmin) {
      fetchAnnouncements();
      fetchCountries();
    }
  }, [sessionLoading, isAdmin, fetchAnnouncements, fetchCountries]);

  const filtered = announcements.filter((a) => {
    const q = searchTerm.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      (a.country_iso || 'all').toLowerCase().includes(q) ||
      a.severity.toLowerCase().includes(q) ||
      a.placement.toLowerCase().includes(q)
    );
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEdit = (row: Announcement) => {
    setEditing(row);
    setForm({
      title: row.title,
      summary: row.summary,
      detail: row.detail || '',
      severity: row.severity,
      country_iso: row.country_iso || '',
      placement: row.placement || 'all_filing',
      is_active: row.is_active,
      starts_at: toLocalInputValue(row.starts_at),
      ends_at: toLocalInputValue(row.ends_at),
      display_order: row.display_order ?? 0,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleFormChange = <K extends keyof AnnouncementFormData>(field: K, value: AnnouncementFormData[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || isEmptyHtml(form.summary)) {
      alert('Title and summary are required.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        summary: form.summary.trim(),
        detail: isEmptyHtml(form.detail) ? null : form.detail.trim(),
        severity: form.severity,
        country_iso: form.country_iso || null,
        placement: form.placement.trim() || 'all_filing',
        is_active: form.is_active,
        starts_at: fromLocalInputValue(form.starts_at),
        ends_at: fromLocalInputValue(form.ends_at),
        display_order: Number(form.display_order) || 0,
      };

      let error;
      if (editing) {
        ({ error } = await supabase.from('announcements').update(payload).eq('id', editing.id));
      } else {
        ({ error } = await supabase.from('announcements').insert([payload]));
      }

      if (error) {
        console.error('Error saving announcement:', error);
        alert(`Failed to ${editing ? 'update' : 'create'} announcement.`);
        return;
      }

      closeModal();
      fetchAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      alert(`Failed to ${editing ? 'update' : 'create'} announcement.`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row: Announcement) => {
    const { error } = await supabase
      .from('announcements')
      .update({ is_active: !row.is_active })
      .eq('id', row.id);
    if (error) {
      console.error('Error toggling announcement:', error);
      alert('Failed to update status.');
      return;
    }
    fetchAnnouncements();
  };

  const handleDelete = async (row: Announcement) => {
    if (!confirm(`Delete announcement "${row.title}"?`)) return;
    const { error } = await supabase.from('announcements').delete().eq('id', row.id);
    if (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement.');
      return;
    }
    fetchAnnouncements();
  };

  if (!sessionLoading && !isAdmin) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="max-w-3xl mx-auto py-12 text-center text-gray-600">
            You do not have permission to view this page.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="page-header">
            <div className="page-header-left">
              <div className="text-sm text-gray-500 mb-1">Admin Only</div>
              <h1 className="page-title">Announcements</h1>
            </div>
            <div className="page-header-right flex gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin/customers')}
                className="f-btn f-btn-primary"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Customers</span>
              </button>
              <button type="button" onClick={() => fetchAnnouncements()} className="f-btn f-btn-success">
                Refresh
              </button>
              <button type="button" onClick={openCreate} className="f-btn f-btn-primary">
                + New Announcement
              </button>
            </div>
          </div>

          <div className="page-card">
            <div className="flex items-center justify-between mb-6">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search title, summary, country, severity..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div className="text-sm text-gray-500">
                {filtered.length} announcement{filtered.length !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="page-table">
                <thead className="page-table-thead">
                  <tr className="page-table-tr">
                    <th className="page-table-th">Title</th>
                    <th className="page-table-th">Severity</th>
                    <th className="page-table-th">Country</th>
                    <th className="page-table-th">Placement</th>
                    <th className="page-table-th">Order</th>
                    <th className="page-table-th">Status</th>
                    <th className="page-table-th">Actions</th>
                  </tr>
                </thead>
                <tbody className="page-table-tbody">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="page-table-td text-center py-8">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                          <span className="ml-2 text-gray-500">Loading announcements...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="page-table-td text-center py-8 text-gray-500">
                        No announcements found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row) => (
                      <tr key={row.id} className="page-table-tr">
                        <td className="page-table-td">
                          <div className="font-medium text-gray-900">{row.title}</div>
                          <div className="text-xs text-gray-500 max-w-xs truncate">{stripHtml(row.summary)}</div>
                        </td>
                        <td className="page-table-td">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${severityBadge(row.severity)}`}>
                            {row.severity}
                          </span>
                        </td>
                        <td className="page-table-td">{row.country_iso || 'All'}</td>
                        <td className="page-table-td">{row.placement}</td>
                        <td className="page-table-td">{row.display_order}</td>
                        <td className="page-table-td">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(row)}
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              row.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {row.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="page-table-td">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <style>{`
            .tox.tox-tinymce-aux,
            .tox-tinymce-aux,
            .tox-menu,
            .tox-dialog-wrap {
              z-index: 10050 !important;
            }
          `}</style>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl flex flex-col max-h-[92vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing ? 'Edit Announcement' : 'New Announcement'}
              </h3>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
                {/* Left: content editing */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 border-b lg:border-b-0 lg:border-r border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => handleFormChange('title', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Summary (banner line) *</label>
                    <p className="text-xs text-gray-500 mb-1">Shown on the colored banner. Use bold/italic for emphasis.</p>
                    <Editor
                      key={`summary-${editing?.id || 'new'}`}
                      tinymceScriptSrc={`${process.env.PUBLIC_URL || ''}/tinymce/tinymce.min.js`}
                      licenseKey="gpl"
                      value={form.summary}
                      onEditorChange={(content) => handleFormChange('summary', content)}
                      init={announcementEditorInit(130)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Detail (modal body)</label>
                    <p className="text-xs text-gray-500 mb-1">Optional. Shown when user clicks See details.</p>
                    <Editor
                      key={`detail-${editing?.id || 'new'}`}
                      tinymceScriptSrc={`${process.env.PUBLIC_URL || ''}/tinymce/tinymce.min.js`}
                      licenseKey="gpl"
                      value={form.detail}
                      onEditorChange={(content) => handleFormChange('detail', content)}
                      init={announcementEditorInit(320)}
                    />
                  </div>
                </div>

                {/* Right: settings */}
                <div className="w-full lg:w-72 xl:w-80 flex-shrink-0 overflow-y-auto px-5 py-5 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Settings</h4>

                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <label className="w-[88px] flex-shrink-0 text-xs font-medium text-gray-600">Severity</label>
                      <span className="text-xs text-gray-400">:</span>
                      <select
                        value={form.severity}
                        onChange={(e) => handleFormChange('severity', e.target.value as Severity)}
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="info">Info (blue)</option>
                        <option value="warning">Warning (amber)</option>
                        <option value="urgent">Urgent (red)</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="w-[88px] flex-shrink-0 text-xs font-medium text-gray-600">Country</label>
                      <span className="text-xs text-gray-400">:</span>
                      <select
                        value={form.country_iso}
                        onChange={(e) => handleFormChange('country_iso', e.target.value)}
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">All countries</option>
                        {countries.map((c) => (
                          <option key={c.iso} value={c.iso}>
                            {c.name} ({c.iso})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="w-[88px] flex-shrink-0 text-xs font-medium text-gray-600">Placement</label>
                      <span className="text-xs text-gray-400">:</span>
                      <select
                        value={form.placement}
                        onChange={(e) => handleFormChange('placement', e.target.value)}
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="all_filing">All Filing</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="w-[88px] flex-shrink-0 text-xs font-medium text-gray-600">Order</label>
                      <span className="text-xs text-gray-400">:</span>
                      <input
                        type="number"
                        value={form.display_order}
                        onChange={(e) => handleFormChange('display_order', Number(e.target.value))}
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="w-[88px] flex-shrink-0 text-xs font-medium text-gray-600">Starts at</label>
                      <span className="text-xs text-gray-400">:</span>
                      <input
                        type="datetime-local"
                        value={form.starts_at}
                        onChange={(e) => handleFormChange('starts_at', e.target.value)}
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="w-[88px] flex-shrink-0 text-xs font-medium text-gray-600">Ends at</label>
                      <span className="text-xs text-gray-400">:</span>
                      <input
                        type="datetime-local"
                        value={form.ends_at}
                        onChange={(e) => handleFormChange('ends_at', e.target.value)}
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex items-start gap-2 pt-1">
                      <label className="w-[88px] flex-shrink-0 text-xs font-medium text-gray-600 pt-0.5">Active</label>
                      <span className="text-xs text-gray-400 pt-0.5">:</span>
                      <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.is_active}
                          onChange={(e) => handleFormChange('is_active', e.target.checked)}
                          className="rounded border-gray-300 mt-0.5"
                        />
                        <span>Visible when within schedule</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 px-6 py-4 border-t flex-shrink-0 bg-white">
                <button type="button" onClick={closeModal} className="f-btn" disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="f-btn f-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Save changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
